import { useId } from 'react';

interface LogoProps {
  /** rendered size in px (the artwork is square) */
  size?: number;
  /**
   * 'idle'  - plays twice on load, then again whenever the parent is hovered
   * 'loop'  - never stops
   * 'still' - no motion at all
   */
  motion?: 'idle' | 'loop' | 'still';
  className?: string;
}

/**
 * The PlaySlot mark: a paddle whose face carries the two slots from the
 * wordmark, with a ball rallying in and out.
 *
 * Gradient ids are scoped with useId so several logos can sit on one page
 * without the second one stealing the first one's fill.
 */
export default function Logo({ size = 34, motion = 'idle', className }: LogoProps) {
  const uid = useId().replace(/:/g, '');
  const face = `face-${uid}`;
  const handle = `handle-${uid}`;

  return (
    <svg
      className={`ps-logo ps-logo--${motion}${className ? ` ${className}` : ''}`}
      viewBox="0 0 64 64"
      width={size}
      height={size}
      role="img"
      aria-label="PlaySlot"
    >
      <defs>
        <linearGradient id={face} x1="0.1" y1="0" x2="0.9" y2="1">
          <stop offset="0%" stopColor="#DBFF75" />
          <stop offset="55%" stopColor="#9BE000" />
          <stop offset="100%" stopColor="#2FE0D6" />
        </linearGradient>
        <linearGradient id={handle} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#4C7DFF" />
          <stop offset="100%" stopColor="#3B63D8" />
        </linearGradient>
      </defs>

      {/* paddle: face, the two slots, handle */}
      <g className="ps-logo__paddle">
        <rect x="24.5" y="40" width="11" height="19" rx="5.5" fill={`url(#${handle})`} />
        <circle cx="30" cy="24" r="18" fill={`url(#${face})`} />
        <rect x="20" y="15" width="7" height="18" rx="3.5" fill="#070809" />
        <rect className="ps-logo__slot" x="31" y="15" width="7" height="12" rx="3.5" fill="#070809" />
      </g>

      <circle className="ps-logo__ball" cx="49" cy="41" r="6" fill="#2FE0D6" />
    </svg>
  );
}
